import { animate, style, transition, trigger } from '@angular/animations';
import { Component, computed, input, output } from '@angular/core';
import { ArticleSummaryView } from '../../../_generated';
import { MessagesListingMessageComponent } from './message/message-listing-message.component';

export interface MessageListingType {
  id: number;
  content: string;
  isFromUser: boolean;
  chatMessageArticles: {
    article: ArticleSummaryView;
  }[];
}

@Component({
  selector: 'dmcom-messages-listing',
  standalone: true,
  imports: [MessagesListingMessageComponent],
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease-in', style({ opacity: 1 })),
      ]),
    ]),
  ],
  template: `
    <div class="flex flex-col gap-24 pt-52 pb-20">
      <!-- LOADING MESSAGES -->
      @if (messageCount(); as messageCount) {
        @if (isLoading()) {
          @for (
            _ of [].constructor(messageCount);
            track $index;
            let i = $index
          ) {
            <dmcom-messages-listing-message
              [isFromUser]="i % 2 !== 0"
              [isLoading]="true"
              [messageId]="i"
              [messageContent]="loadingMessage()"
              [messageArticles]="[]"
            />
          }
        } @else {
          @for (
            message of messagesWithLastMessageFromChatbot();
            let i = $index;
            track i
          ) {
            <dmcom-messages-listing-message
              @fadeIn
              [id]="'message-' + message.id"
              [isFromUser]="message.isFromUser"
              [messageId]="message.id"
              [messageContent]="message.content"
              [isProcessing]="isThinking()"
              [isMobile]="isMobile()"
              [messageArticles]="message.chatMessageArticles"
              (openArticleClicked)="openArticleClicked.emit($event)"
            />
          }
        }
      }

      <!-- AI IS THINKING -->
      @if (isThinking()) {
        <div class="flex justify-start">
          <dmcom-messages-listing-message
            [messageId]="-1"
            [messageContent]="''"
            [isFromUser]="false"
            [isThinking]="true"
            [messageArticles]="[]"
          />
        </div>
      }
    </div>
  `,
})
export class MessagesListingComponent {
  public readonly messages = input.required<MessageListingType[]>();
  public readonly messagesWithLastMessageFromChatbot = computed(() => {
    const messages = this.messages();
    // Filter out the first message if it's not from the user (assistant description)
    const filteredMessages = messages[0]?.isFromUser
      ? messages
      : messages.slice(1);
    const lastMessageFromChatbot = filteredMessages
      .filter((message) => !message.isFromUser)
      .at(-1);

    return messages.map((message) => ({
      ...message,
      isLastMessageFromChatbot: message.id === lastMessageFromChatbot?.id,
    }));
  });
  public readonly isLoading = input.required<boolean>();
  public readonly isThinking = input.required<boolean>();
  public readonly isMobile = input.required<boolean>();
  public readonly messageCount = input.required<number>();
  public readonly loadingMessage = computed(() => {
    return 'Loading...';
  });
  public readonly openArticleClicked = output<number>();

  openArticle(articleId: number) {
    this.openArticleClicked.emit(articleId);
  }
}
